import type { DocumentResource } from "@/lib/document-resource";
import { DocumentThumbnail } from "./DocumentThumbnail";
import { DocumentOpenButton } from "./DocumentOpenButton";
import { useEffect, useState, type DragEvent, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { ExtRenameInput } from "./ExtRenameInput";
import { DocPageIcon, DownloadIcon } from "./icons";

// List-view preview card (discovery-style): a server-rendered thumbnail of the
// document (genesis Document.preview) with name + date below. CV drafts have no
// server preview (rendered client-side in the editor) and fall back to a
// placeholder; clicking a CV card opens the editor.
export function DocPreviewCard({
  resource,
  editor,
  previewSrc,
  cvGenerator,
  kind,
  name,
  when,
  sourceDisplay,
  isNew,
  isRenaming,
  selected,
  onToggleSelect,
  onStartRename,
  onRename,
  onStopRename,
  onOpen,
  onDownload,
  draggable,
  onDragStart,
  onDragEnd,
  disabled = false,
  documentId,
  loadPreview,
  idPrefix,
}: {
  // Server-rendered thumbnail (data URL) or null → placeholder.
  resource?: DocumentResource;
  editor?: boolean;
  previewSrc: string | null;
  // CV-Generator entries get the generator's own glyph as placeholder instead
  // of the generic file icon.
  cvGenerator?: boolean;
  kind: string;
  name: string;
  when: string | null;
  source: string;
  sourceDisplay?: ReactNode;
  // Created within the recent window → small accent "new" dot.
  isNew?: boolean;
  // This card is currently in rename mode (only one row at a time, parent-driven).
  isRenaming: boolean;
  selected: boolean;
  onToggleSelect?: () => void;
  // Click the name → caller flips this card into rename mode.
  onStartRename?: () => void;
  onRename: (newName: string) => void;
  // Blur / Enter / Escape on the rename input → caller exits rename mode.
  onStopRename: () => void;
  onOpen: () => void;
  onDownload?: () => void;
  draggable?: boolean;
  onDragStart?: (e: DragEvent) => void;
  onDragEnd?: () => void;
  disabled?: boolean;
  documentId?: string;
  loadPreview?: (id: string) => Promise<string | null>;
  idPrefix?: string;
}) {
  const intl = useIntl();
  // ISO "2026-06-03" → "03.06.2026"
  const day = when ? String(when).slice(0, 10).split("-").reverse().join(".") : "";
  // JSON has no meaningful server thumbnail (genesis returns a non-image
  // preview → broken "?"). Show a "{}" glyph for JSON, and fall back to the
  // same glyph if any server thumbnail fails to load.
  const isJson = kind?.toUpperCase() === "JSON";
  const [imgFailed, setImgFailed] = useState(false);
  const [loadedPreview, setLoadedPreview] = useState<{ id: string; src: string | null } | null>(null);
  useEffect(() => {
    if (resource || previewSrc || !loadPreview || !documentId) return;
    let active = true;
    void loadPreview(documentId).then(src => {
      if (active) setLoadedPreview({ id: documentId, src });
    }).catch(() => { /* A missing thumbnail keeps the file glyph; opening still reports errors. */ });
    return () => { active = false; };
  }, [resource, documentId, loadPreview, previewSrc]);
  const imageSrc = previewSrc ?? (loadedPreview?.id === documentId ? loadedPreview?.src : null);
  const showImage = Boolean(imageSrc) && !isJson && !imgFailed;

  return (
    <div
      draggable={draggable}
      data-state-mark-position="start"
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={`group relative flex flex-col overflow-hidden rounded-lg bg-surface ring-1 transition ${
        selected ? `ring-2 ring-accent-decoration ${onToggleSelect ? "" : "smycles-selected"}` : "ring-ink/10 hover:ring-ink/15"
      } ${draggable ? "cursor-grab active:cursor-grabbing" : ""}`}
    >
      {/* Selection checkbox: hidden at rest on hover-capable devices, revealed
          on card hover or when checked. Touch devices (no hover) always show
          it via `pointer-coarse:opacity-100`. */}
      {onToggleSelect && <input
        type="checkbox"
        disabled={disabled}
        checked={selected}
        onChange={onToggleSelect}
        aria-label={intl.formatMessage(
          { id: "service.docPreviewCard.selectAriaLabel", defaultMessage: "Select {name}" },
          { name },
        )}
        className={`absolute left-1.5 top-1.5 z-10 h-5 w-5 cursor-pointer accent-accent transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-ui ${
          selected
            ? "opacity-100"
            : "opacity-0 group-hover:opacity-100 focus:opacity-100 pointer-coarse:opacity-100"
        }`}
      />}
      {isNew ? (
        <span
          title={intl.formatMessage({ id: "service.docPreviewCard.newBadge", defaultMessage: "New" })}
          className="absolute right-1.5 top-1.5 z-10 h-2.5 w-2.5 rounded-full bg-accent"
          aria-hidden="true"
        />
      ) : null}
      <button
        type="button"
        disabled={disabled || (!editor && Boolean(resource?.unavailableReason))}
          onClick={onOpen}
        title={intl.formatMessage({ id: !editor && !cvGenerator && resource?.previewable ? "documents.preview.open" : "service.docPreviewCard.open" })}
        aria-label={!editor && !cvGenerator && resource?.previewable ? `${intl.formatMessage({ id: "documents.preview.open" })}: ${name}` : undefined}
        className="relative flex h-[140px] w-full items-center justify-center overflow-hidden bg-ink/[0.06]"
      >
        <DocumentThumbnail resource={resource} previewSrc={showImage ? imageSrc : null} onImageError={() => setImgFailed(true)} fallback={
          isJson || imgFailed ? (
          // JSON / unrenderable preview: "{}" glyph instead of a broken image.
          <div className="flex h-12 w-12 items-center justify-center rounded-md bg-ink/10 font-mono text-smycles-title text-ink-muted">
            {"{ }"}
          </div>
        ) : cvGenerator ? (
          // CV-Generator entries have no server thumbnail: show the generator's
          // own glyph (same as the CV-Generator action card) in an accent tile,
          // labelled so the tile reads as an editable draft.
          <div className="flex flex-col items-center gap-1.5">
            <div className="flex h-12 w-12 items-center justify-center rounded-md bg-accent/15 text-smycles-display-s text-ink-accent">
              ▣
            </div>
            <span className="text-smycles-micro leading-tight text-ink">
              <FormattedMessage
                id="service.docPreviewCard.cvGeneratorDraft"
                defaultMessage="CV-Generator draft"
              />
            </span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1 text-ink-muted">
            <DocPageIcon />
          </div>
        )
        } />
        <span className="absolute bottom-1 left-1 rounded bg-ink px-1 py-px text-smycles-micro font-medium uppercase tracking-wider text-ink">
          {kind}
        </span>
      </button>
      <div className="flex items-start justify-between gap-1 px-2 py-1.5">
        <div className="min-w-0 flex-1">
          {isRenaming ? (
            <ExtRenameInput
              name={name}
              onRename={onRename}
              onBlur={onStopRename}
              autoFocus
              className={`w-full min-w-0 rounded bg-surface px-1 py-0.5 text-smycles-label ${isNew ? "font-semibold" : "font-medium"} text-ink ring-1 ring-ink/15 focus:outline-none focus:ring-ink-ui`}
            />
          ) : (
            onStartRename ? <button
              id={idPrefix && `${idPrefix}-name`}
              type="button"
              disabled={disabled}
              onClick={onStartRename}
              className={`block w-full truncate text-left text-smycles-label ${isNew ? "font-semibold" : "font-medium"} transition hover:text-ink-accent`}
              title={intl.formatMessage({ id: "service.docPreviewCard.rename", defaultMessage: "Click to rename" })}
            >
              {name}
            </button> : <span id={idPrefix && `${idPrefix}-name`} className={`smycles-field-meta block truncate ${isNew ? "font-semibold" : "font-medium"}`}>{name}</span>
          )}
          <div className="truncate text-smycles-micro text-ink">{day}</div>
          {sourceDisplay && <div className="mt-2">{sourceDisplay}</div>}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {onDownload ? (
            <Button
              id={idPrefix && `${idPrefix}-download`}
              variant="icon"
              size="sm"
              disabled={disabled}
            onClick={onDownload}
              aria-label={intl.formatMessage({
                id: "service.docPreviewCard.download",
                defaultMessage: "Download",
              })}
              title={intl.formatMessage({
                id: "service.docPreviewCard.download",
                defaultMessage: "Download",
              })}
            >
              <DownloadIcon />
            </Button>
          ) : null}
          <DocumentOpenButton id={idPrefix && `${idPrefix}-open`} resource={resource} name={name} editor={editor || cvGenerator} disabled={disabled} onOpen={onOpen} />
        </div>
      </div>
    </div>
  );
}
